import React, { useMemo } from 'react';

import { Spinner } from '../PageLoader';

type DealButtonProps = {
  label: string;
  children?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  Icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  variant?: 'primary' | 'success' | 'reject' | 'dark' | 'success_secondary';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
  /** Shown next to the spinner when `loading` is true (default: Loading...) */
  loadingLabel?: string;
};

const variantClasses = {
  primary: 'text-brand-950 bg-brand-400 hover:text-white hover:bg-brand-800 border-none',
  success: 'text-brand-950 bg-green-400 hover:text-white hover:bg-green-600 border-none',
  success_secondary: 'text-brand-950 bg-green-400 hover:text-white hover:bg-green-600 border-none',
  reject: 'text-red-400 hover:bg-red-400 hover:text-white border-red-400',
  dark: 'text-white bg-grey-950 border-gray-700 hover:bg-background-canvas hover:border-red-500',
};

const disabledVariantClasses = {
  primary: 'text-gray-500 bg-gray-800 border-none',
  success: 'text-gray-600 bg-green-300 border-none opacity-60',
  success_secondary: 'text-gray-600 bg-gray-600 border-none opacity-60',
  reject: 'text-red-300 border-red-300 opacity-60',
  dark: 'text-gray-400 bg-gray-800 border-gray-600 opacity-60',
};

const DealButton: React.FC<DealButtonProps> = ({
  label,
  children,
  onClick,
  className = '',
  Icon,
  iconPosition = 'left',
  variant = 'primary',
  disabled = false,
  type = 'button',
  loading = false,
  loadingLabel = 'Loading...',
}) => {
  const baseClasses = useMemo(
    () =>
      `inline-flex items-center gap-4 py-2.5 px-4 rounded font-heebo text-sm font-medium border
       transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
       ${disabled || loading ? disabledVariantClasses[variant] : variantClasses[variant]} 
       ${className}`,
    [variant, className, disabled, loading],
  );

  const disabledClasses = disabled || loading ? 'hover:cursor-not-allowed' : '';

  // Get the text color for the spinner based on variant and state
  const getSpinnerColor = () => {
    if (disabled || loading) {
      switch (variant) {
        case 'success':
        case 'success_secondary':
          return '#6b7280'; // gray-600
        case 'reject':
          return '#fca5a5'; // red-300
        case 'dark':
          return '#9ca3af'; // gray-400
        default:
          return '#6b7280'; // gray-500
      }
    }
    // Not disabled/loading - use normal text colors
    switch (variant) {
      case 'success':
      case 'success_secondary':
        return '#0f172a'; // brand-950
      case 'reject':
        return '#f87171'; // red-400
      case 'dark':
        return '#ffffff'; // white
      default:
        return '#0f172a'; // brand-950
    }
  };

  return (
    <button
      aria-label={label}
      onClick={disabled || loading ? undefined : onClick}
      className={`${baseClasses} ${disabledClasses}`}
      disabled={disabled || loading}
      type={type}
    >
      {loading ? (
        <>
          <Spinner loading={true} size={16} color={getSpinnerColor()} />
          <span>{loadingLabel}</span>
        </>
      ) : (
        <>
          {iconPosition === 'left' && Icon}
          {children}
          {iconPosition === 'right' && Icon}
        </>
      )}
    </button>
  );
};

export default React.memo(DealButton);
